// ────────────────────────────────────────────────────────────────────────────
// Organisation-details band inspector.
//
// Each field is a tick box (does it print?) plus an edit box holding the real
// text, so it can be edited directly — merge the address onto fewer lines, add
// a comma, change "VAT" to "VAT Reg. No.", whatever the document needs.
//
// The box starts out showing the organisation's own value but stays *unbound*
// until the operator actually changes it: no override is stored, so the
// template keeps following Settings → Organisation and picks up a later change
// to the address. The first edit breaks that link deliberately, and the note
// under the box says which state it is in.
//
// Typing the record's value back in (or clearing the box) drops the override
// and restores the link, rather than freezing a copy that happens to match.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AlignPicker, Checkbox, InspectorGroup, InspectorRow, MoveButtons } from './controls';
import { moveItem } from '../lib/arrays';
import {
  defaultOrganisationFieldText,
  hasOverride,
  withOverride,
} from '../lib/organisationFields';
import type { DocumentOrganisation } from '../lib/documentData';
import {
  ORGANISATION_FIELDS,
  ORGANISATION_FIELD_LABEL,
  type OrganisationBand,
  type OrganisationField,
} from '../lib/types';

function FieldEditor({
  field,
  band,
  organisation,
  index,
  onChange,
}: {
  field: OrganisationField;
  band: OrganisationBand;
  organisation: DocumentOrganisation;
  index: number;
  onChange: (next: OrganisationBand) => void;
}) {
  const defaultText = defaultOrganisationFieldText(field, organisation);
  const override = band.overrides?.[field];
  const overridden = hasOverride(field, band.overrides);

  // Local edit state, seeded from the override (or the record). It has to be
  // local: if the textarea read straight off the band, deleting back to empty
  // would clear the override, re-resolve to the record's value, and refill the
  // box mid-keystroke — so you could never actually empty it and retype.
  const [draft, setDraft] = useState(override ?? defaultText);

  // Re-seed only when the field or the underlying record text changes (band
  // switch, or someone edits the organisation). Deliberately NOT keyed on the
  // override: that would fire on every keystroke and undo the point above.
  useEffect(() => {
    setDraft(band.overrides?.[field] ?? defaultText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, defaultText]);

  const commit = (next: string) => {
    setDraft(next);
    // Matching the record exactly is not an override — storing it would freeze
    // a copy that silently stops tracking Settings → Organisation.
    const store = next.trim() === defaultText.trim() ? '' : next;
    onChange({ ...band, overrides: withOverride(band.overrides, field, store) });
  };

  const reset = () => {
    setDraft(defaultText);
    onChange({ ...band, overrides: withOverride(band.overrides, field, '') });
  };

  // A field the organisation record has nothing for still prints if the
  // operator types something — worth saying so, otherwise an empty box looks
  // broken.
  const noRecordValue = defaultText.length === 0;

  return (
    <div className="rounded border border-slate-200 bg-white p-1.5">
      <div className="flex items-center justify-between gap-1">
        <Checkbox
          checked
          label={ORGANISATION_FIELD_LABEL[field]}
          onChange={() => onChange({ ...band, fields: band.fields.filter((f) => f !== field) })}
        />
        <div className="flex items-center gap-1">
          {overridden && (
            <button
              type="button"
              title="Go back to the organisation's value"
              aria-label={`Reset ${ORGANISATION_FIELD_LABEL[field]} to the organisation value`}
              onClick={reset}
              className="text-slate-400 hover:text-sky-700"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
          <MoveButtons
            canUp={index > 0}
            canDown={index < band.fields.length - 1}
            onUp={() => onChange({ ...band, fields: moveItem(band.fields, index, -1) })}
            onDown={() => onChange({ ...band, fields: moveItem(band.fields, index, 1) })}
          />
        </div>
      </div>

      <textarea
        value={draft}
        rows={field === 'addressLines' ? 4 : 1}
        placeholder={noRecordValue ? 'Not set in Settings → Organisation' : undefined}
        onChange={(e) => commit(e.target.value)}
        // Leaving an emptied box shows the record's value again, since that is
        // what will actually print.
        onBlur={() => {
          if (!draft.trim() && !noRecordValue) setDraft(defaultText);
        }}
        className={cn(
          'mt-1 w-full resize-y rounded border p-1.5 text-xs text-slate-800 focus:outline-none',
          overridden
            ? 'border-sky-300 bg-sky-50/40 focus:border-sky-400'
            : 'border-slate-200 placeholder:text-slate-400 focus:border-sky-400',
        )}
      />

      <p className="mt-0.5 text-[10px] text-slate-400">
        {overridden
          ? 'Edited for this layout.'
          : noRecordValue
            ? 'Type a value to print one anyway.'
            : 'Following Settings → Organisation.'}
      </p>
    </div>
  );
}

export function OrganisationBandInspector({
  band,
  organisation,
  onChange,
}: {
  band: OrganisationBand;
  /** The live organisation — the source of each field's starting text. */
  organisation: DocumentOrganisation;
  onChange: (next: OrganisationBand) => void;
}) {
  const unselected = ORGANISATION_FIELDS.filter((f) => !band.fields.includes(f));

  return (
    <>
      <InspectorGroup title="Layout">
        <InspectorRow label="Align">
          <AlignPicker value={band.align} onChange={(align) => onChange({ ...band, align })} />
        </InspectorRow>
      </InspectorGroup>

      <InspectorGroup title="Fields (print order)">
        <div className="space-y-1.5">
          {band.fields.map((field, i) => (
            <FieldEditor
              key={field}
              field={field}
              band={band}
              organisation={organisation}
              index={i}
              onChange={onChange}
            />
          ))}
        </div>

        {unselected.length > 0 && (
          <div className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
            {unselected.map((field) => (
              <div key={field} className="px-1">
                <Checkbox
                  checked={false}
                  label={ORGANISATION_FIELD_LABEL[field]}
                  onChange={() => onChange({ ...band, fields: [...band.fields, field] })}
                />
              </div>
            ))}
          </div>
        )}

        <p className="mt-2 text-[10px] leading-snug text-slate-400">
          Each line in a box prints as its own line. Clear a box to go back to the organisation
          value; untick a field to leave the line out. Edits here affect this layout only — they
          never change Settings → Organisation.
        </p>
      </InspectorGroup>
    </>
  );
}
